// Standard library imports
import React, { useEffect, useState } from 'react';

// External library imports
import { toast } from 'react-toastify';
import { Form, Formik } from 'formik';
import moment from 'moment';

// Internal module imports
import classes from '../../../../styles/AllDevices.module.css';
import Dropdown from '../../../../components/Inputs/Dropdown';
import { Input } from '../../../../components/Inputs/Input';
import { useLoader } from '../../../../hooks';
import { regulationsService } from '../../../../services/regulationsService';
import { DownloadAsExcel, DownloadAsExcelWithBuffer } from '../../../../utils/downloadAsExcel';
import { signalsServiceForRoot } from '../../../../services/signalsServiceForRoot';
import { momentTimeFormater } from '../../../../utils/dateHelper';
import { PQDownloadValidation } from '../../../../validations/Root/PQDownloadValidation';

const PQDownload = ({ type, startTime, endTime, date, signalId, signalName, setPQModal }) => {
    const [startLoader, stopLoader] = useLoader();
    const [marketOptions, setMarketOptions] = useState([]);

    useEffect(() => {
        getMarkets();
    }, []);

    const getMarkets = () => {
        regulationsService.ReadAll(startLoader, handleSuccess, handleError, stopLoader);
    };

    const handleSuccess = ({ data }) => {
        const temp = [{ label: 'None', value: '' }];
        data.data.map((item) => {
            temp.push({
                label: item.name,
                value: item.name,
            });
        });
        setMarketOptions(temp);
    };

    const handleSubmit = (value) => {
        let params = {
            start: startTime,
            end: endTime,
            date: moment(date).tz('Europe/Berlin').format('YYYY-MM-DD'),
            signalId: signalId,
            market: value.market,
            ...(type === 'PQDownload' ? { capacity: value.capacity } : {}),
            type: type,
        };

        signalsServiceForRoot.downLoadEnergynetAudit(
            params,
            () => startLoader('read-data'),
            type === 'PQDownload' ? handlePQDownload : handleDownloadEnergynetAudit,
            handleError,
            () => stopLoader('read-data')
        );
    };

    const handlePQDownload = ({ data }) => {
        let formatedData = [];
        let fiveAndTwentyPercent = false;
        if (data.data.data[0].hasOwnProperty('fivePercent')) {
            fiveAndTwentyPercent = true;
        }
        data.data.data.map((e) => {
            let temp = {
                date: momentTimeFormater(e.date).format('DD/MM/YYYY'),
                time: e.time,
                frequency: e.frequency,
                ...(fiveAndTwentyPercent && {
                    '-5%': e.fivePercent,
                }),
                load: e.load,
                ...(fiveAndTwentyPercent && {
                    '+20%': e.twentyPercent,
                }),
            };
            formatedData.push(temp);
        });
        let headers = ['Date (CET)', 'Time (CET)', 'Frequency', 'Load'];
        if (fiveAndTwentyPercent) {
            headers = ['Date (CET)', 'Time (CET)', 'Frequency', '-5%', 'Load', '+20% '];
        }
        data.data.data.length && DownloadAsExcel(formatedData, `${data.data?.instanceName} PQ Audit`, headers);
    };

    const handleDownloadEnergynetAudit = ({ data }) => {
        let formatedData = [];
        data.data.data.map((e) => {
            let temp = {
                date: momentTimeFormater(e.date).format('DD/MM/YYYY'),
                time: e.time,
                frequency: e.frequency,
                capacitySold: e.soldCapacity,
                targetCapacity: e.targetCapacity,
                '-5%': e.minusFivePercent,
                load: e.load,
                '+20%': e.plusTwentyPercent,
                ...(!data.data?.basePower && {
                    deltaLoad: e.deltaLoad,
                    status: e.status,
                }),
            };
            if (data.data?.basePower) {
                temp.basePower = e.basePower;
                temp.deltaLoad = e.deltaLoad;
                temp.status = e.status;
            }
            formatedData.push(temp);
        });

        let headers = ['Date (CET)', 'Time (CET)', 'Frequency', 'Capacity Sold', 'Target Capacity', '-5%', 'Load', '+20% '];

        if (data.data?.basePower) {
            headers.push(...['Base Power', 'Delta Load', 'Status']);
        } else {
            headers.push(...['Delta Load', 'Status']);
        }

        data.data.data.length && DownloadAsExcel(formatedData, `${data.data?.customerName}_${data.data?.instanceName}`, headers);
    };

    const downloadEnerginetFormatData = (value) => {
        const params = {
            id: signalId,
            start: startTime,
            end: endTime,
            date: momentTimeFormater(date).format('YYYY-MM-DD'),
            market: value.market,
        };
        signalsServiceForRoot.getEnerginetFormatData(params, startLoader, handleEnerginetFormatDataSuccess, handleError, stopLoader);
    };

    const handleEnerginetFormatDataSuccess = ({ data }) => {
        const base64String = data.data.base64String;
        const templateBase64String = data.data.templateBase64String;
        DownloadAsExcelWithBuffer(base64String, `Energinet Format - ${signalName}`);
    };

    const handleError = (err) => {
        let data = err && err.response ? err.response.data : null;
        if (data) toast.error(data.message);
        else toast.error('Internal server error!');
    };

    return (
        <div>
            <Formik
                initialValues={{
                    market: '',
                    capacity: '',
                }}
                enableReinitialize
                validationSchema={PQDownloadValidation(type)}
                onSubmit={(val) => {
                    type === 'energinetFormat' ? downloadEnerginetFormatData(val) : handleSubmit(val);
                }}
            >
                {({ errors, touched, values, isValidating, ...props }) => (
                    <Form>
                        <div>
                            <div className={classes.FieldControl2}>
                                <label for="market">Market</label>
                                <Dropdown name="market" options={marketOptions} defaultValue={marketOptions[0]} />
                            </div>
                            {type === 'PQDownload' ? (
                                <div className={classes.FieldControl2}>
                                    <label for="capacity">Capacity</label>
                                    <Input name="capacity" id="capacity" type="number" style={{ marginTop: '0' }} />
                                </div>
                            ) : (
                                ''
                            )}
                        </div>

                        <div className={classes.ButtonContainer}>
                            <div>
                                <button
                                    className="btn-secondary"
                                    onClick={() => {
                                        setPQModal({ status: false, type: '' });
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                            <div>
                                <button className="btn-primary" type="submit">
                                    Submit
                                </button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default PQDownload;
